/**
 * history operations
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// application
import {selectFieldName} from "../redux/types/selectors";
import {fetchJSON} from "../operations/communication";
import {
    REST_CALL_GET_OBJECT_HISTORY,
    REST_CALL_GET_GLOBAL_HISTORY,
    REST_SERVER_BASE_URL
} from "../config";

// 3rd party
import _ from 'lodash';


function describeLength(n) {
    if (n === 1) return "1 Element";
    else return n + " Elemente";
}

function describeValue(value) {
    if (_.isNil(value)) {
        return "nicht vorhanden";
    }
    else if (_.isString(value)) {
        if (value.length > 500) return '"' + value.substr(0, 500) + '..."';
        else return '"' + value + '"';
    }
    else if (_.isNumber(value)) {
        return value;
    }
    else if (_.isArray(value)) {
        return describeLength(value.length);
    }
    else if (_.isObject(value)) {
        return describeLength(Object.keys(value).length);
    }
    else {
        return "?";
    }
}

function describeChange(oldValue, newValue) {
    if (_.isNil(newValue)) {
        return describeValue(oldValue) + " gelöscht";
    }
    else if (_.isNil(oldValue)) {
        return describeValue(newValue) + " ergänzt";
    }
    else if (_.isString(newValue) || _.isNumber(newValue)) {
        return describeValue(oldValue) + " zu " + describeValue(newValue) + " geändert";
    }
    else if (_.isArray(newValue)) {
        if (_.isArray(oldValue)) {
            const delta = newValue.length - oldValue.length;
            if (delta < 0) {
                return describeLength(-delta) + " entfernt";
            }
            else if (delta > 0) {
                return describeLength(delta) + " ergänzt";
            }
            else {
                return describeLength(oldValue.length) + " bearbeitet";
            }
        }
        else {
            return describeValue(oldValue) + " zu " + describeValue(newValue) + " geändert";
        }
    }
    else if (_.isObject(newValue)) {
        if (_.isObject(oldValue)) {
            const mergedKeys = Object.keys({...oldValue, ...newValue});
            const changedKeys = mergedKeys.filter(key => !_.isEqual(oldValue[key], newValue[key]));
            return changedKeys.map(key => describeChange(oldValue[key], newValue[key])).join(", ");
        }
        else {
            return describeValue(oldValue) + " zu " + describeValue(newValue) + " geändert";
        }
    }
    else {
        return "?";
    }
}

function createChangeItem(typeState, key, objectTypeId, oldValue, newValue, isField) {
    return {
        key: key,
        name: selectFieldName(typeState, objectTypeId, key),
        oldValue: oldValue,
        newValue: newValue,
        description: describeChange(oldValue, newValue),
        isField: isField
    };
}

function createChangeItems(typeState, change) {

    if (!change.previousObject) return undefined;
    const objectTypeId = change.previousObject.type;
    if (!objectTypeId) return undefined;

    // first create items for changed fields
    const fieldItems = Object.keys(change.changedFields).map(key => createChangeItem(
        typeState,
        key,
        objectTypeId,
        change.previousObject.fields ? change.previousObject.fields[key] : undefined,
        change.changedFields[key],
        true
    )).sort((a, b) => a.name.localeCompare(b.name));

    // then create items for other changes (title etc.)
    const valueItems = Object.keys(change.changedValues).map(key => createChangeItem(
        typeState,
        '#' + key,
        objectTypeId,
        change.previousObject[key],
        change.changedValues[key],
        false
    )).sort((a, b) => a.name.localeCompare(b.name));

    return [...fieldItems, ...valueItems];
}

export async function fetchObjectHistory(accessToken, objectId, typeState) {
    const response = await fetchJSON(
        REST_SERVER_BASE_URL + REST_CALL_GET_OBJECT_HISTORY.path.replace(
            "<id>", objectId
        ), {
            method: REST_CALL_GET_OBJECT_HISTORY.method,
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + accessToken
            }
        }
    );
    return response.changes.map(change => ({...change, items: createChangeItems(typeState, change)}))
}

export async function fetchGlobalHistory(accessToken, typeState) {
    const response = await fetchJSON(
        REST_SERVER_BASE_URL + REST_CALL_GET_GLOBAL_HISTORY.path.replace(
            "<index>", 0
        ).replace(
            "<count>", 100
        ), {
            method: REST_CALL_GET_GLOBAL_HISTORY.method,
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + accessToken
            }
        }
    );
    return response.changes.map(change => ({...change, items: createChangeItems(typeState, change)}))
}
